import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabPanel } from "@/components/ui/tabs";
import PaymentHistoryTable from "@/components/billing/PaymentHistoryTable";
import CreditPackageCard from "@/components/billing/CreditPackageCard";
import SpendingChart from "@/components/billing/SpendingChart";
import PaymentMethodCard from "@/components/billing/PaymentMethodCard";
import CreditUsageShell from "@/components/billing/CreditUsageShell";
import PageBackButton from "@/components/PageBackButton";
import { getMySubscription } from "@/api/subscriptions";
import {
  getAIBalance,
  updateAIBalance,
  getAISpendingSummary,
  topUpAICredits,
  getAICreditPackages,
  getAITransactions,
} from "@/api/billing";
import {
  listPaymentMethods,
  listPaymentIntents,
  deletePaymentMethod,
  setDefaultPaymentMethod,
} from "@/api/payments";
import { useSearchParams } from "react-router-dom";
import {
  CreditCard,
  BarChart3,
  Wallet,
  Clock,
  DollarSign,
  Zap,
  Shield,
  Settings,
  RefreshCw,
  Loader2,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

const CREDITS_TAB_KEY = "credits";
const USAGE_TAB_KEY = "usage";
const ACTIVITY_TAB_KEY = "activity";
const METHODS_TAB_KEY = "methods";
const OVERVIEW_TAB_KEY = "overview";
const CREDIT_ACTIVITY_TAB_KEY = "credit-activity";
const PAYMENTS_ACTIVITY_TAB_KEY = "payments";
const LEGACY_CREDITS_TAB_KEYS = new Set(["ai-billing", "ai-credits"]);
const VALID_TAB_KEYS = new Set([
  OVERVIEW_TAB_KEY,
  CREDITS_TAB_KEY,
  USAGE_TAB_KEY,
  ACTIVITY_TAB_KEY,
  METHODS_TAB_KEY,
]);

const normalizeBillingTab = (tab) =>
  LEGACY_CREDITS_TAB_KEYS.has(tab)
    ? CREDITS_TAB_KEY
    : VALID_TAB_KEYS.has(tab)
      ? tab
      : OVERVIEW_TAB_KEY;

const TABS = [
  { key: OVERVIEW_TAB_KEY, label: "Overview", icon: <Wallet className="w-4 h-4" /> },
  {
    key: CREDITS_TAB_KEY,
    label: "Credits",
    icon: <Zap className="w-4 h-4" />,
  },
  {
    key: USAGE_TAB_KEY,
    label: "Usage",
    icon: <BarChart3 className="w-4 h-4" />,
  },
  {
    key: ACTIVITY_TAB_KEY,
    label: "Activity",
    icon: <Clock className="w-4 h-4" />,
  },
  {
    key: METHODS_TAB_KEY,
    label: "Payment Methods",
    icon: <CreditCard className="w-4 h-4" />,
  },
];

const ACTIVITY_TABS = [
  { key: CREDIT_ACTIVITY_TAB_KEY, label: "Credits", icon: <Zap className="w-4 h-4" /> },
  { key: PAYMENTS_ACTIVITY_TAB_KEY, label: "Payments", icon: <CreditCard className="w-4 h-4" /> },
];

const CREDIT_ACTIVITY_FILTERS = [
  { key: "all", label: "All Activity" },
  { key: "added", label: "Added" },
  { key: "used", label: "Used" },
];

const getCreditTransactionGroup = (transactionType) =>
  (transactionType || "").toUpperCase() === "DEBIT" ? "used" : "added";

const getCreditTransactionPresentation = (transactionType, transactionTypeDisplay) => {
  const group = getCreditTransactionGroup(transactionType);

  if (group === "added") {
    return {
      label: "Added",
      variant: "success",
      detail: transactionTypeDisplay || transactionType || "Credit",
      sign: "+",
      amountClassName: "text-success",
    };
  }

  return {
    label: "Used",
    variant: "default",
    detail: transactionTypeDisplay || transactionType || "Debit",
    sign: "-",
    amountClassName: "text-textPrimary",
  };
};

export default function BillingPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = normalizeBillingTab(searchParams.get("tab") || OVERVIEW_TAB_KEY);
  const [activeTab, setActiveTab] = useState(initialTab);
  const [activityTab, setActivityTab] = useState(CREDIT_ACTIVITY_TAB_KEY);

  // ── State ─────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [aiBalance, setAiBalance] = useState(null);
  const [spending, setSpending] = useState(null);
  const [creditPackages, setCreditPackages] = useState([]);
  const [creditTransactions, setCreditTransactions] = useState({ results: [], count: 0 });
  const [paymentHistory, setPaymentHistory] = useState({ results: [], count: 0 });
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);
  const [creditsLoading, setCreditsLoading] = useState(false);
  const [creditActivityLoading, setCreditActivityLoading] = useState(false);
  const [paymentHistoryLoading, setPaymentHistoryLoading] = useState(false);

  // Credit settings state
  const [dailyLimit, setDailyLimit] = useState("");
  const [monthlyLimit, setMonthlyLimit] = useState("");
  const [lowBalanceThreshold, setLowBalanceThreshold] = useState("");
  const [autoPause, setAutoPause] = useState(false);

  // Pagination
  const [creditTxPage, setCreditTxPage] = useState(1);
  const [paymentPage, setPaymentPage] = useState(0);
  const [creditActivityFilter, setCreditActivityFilter] = useState("all");
  const [isAdvancedCreditControlsOpen, setIsAdvancedCreditControlsOpen] = useState(false);
  const advancedCreditControlsRef = React.useRef(null);

  // ── Tab change ────────────────────────────────────────────────────
  const handleTabChange = (key) => {
    setActiveTab(key);
    setSearchParams({ tab: key });
  };

  const openCreditsTab = () => handleTabChange(CREDITS_TAB_KEY);

  const openCreditControls = () => {
    setIsAdvancedCreditControlsOpen(true);
    handleTabChange(CREDITS_TAB_KEY);
  };

  const openCreditActivity = () => {
    setActivityTab(CREDIT_ACTIVITY_TAB_KEY);
    handleTabChange(ACTIVITY_TAB_KEY);
  };

  const openPaymentsActivity = () => {
    setActivityTab(PAYMENTS_ACTIVITY_TAB_KEY);
    handleTabChange(ACTIVITY_TAB_KEY);
  };

  useEffect(() => {
    const tabFromUrl = searchParams.get("tab") || OVERVIEW_TAB_KEY;
    const normalizedTab = normalizeBillingTab(tabFromUrl);

    if (tabFromUrl !== normalizedTab) {
      setSearchParams({ tab: normalizedTab }, { replace: true });
    }

    setActiveTab(normalizedTab);
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (activeTab !== CREDITS_TAB_KEY || !isAdvancedCreditControlsOpen) {
      return;
    }

    advancedCreditControlsRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }, [activeTab, isAdvancedCreditControlsOpen]);

  // ── Data fetching ─────────────────────────────────────────────────
  const fetchOverview = useCallback(async () => {
    try {
      const [subData, balData, spendData] = await Promise.all([
        getMySubscription().catch(() => null),
        getAIBalance().catch(() => null),
        getAISpendingSummary(30).catch(() => null),
      ]);
      setSubscription(subData?.status !== "none" ? subData : null);
      setAiBalance(balData);
      setSpending(spendData);
      if (balData) {
        setDailyLimit(balData.daily_limit ?? "");
        setMonthlyLimit(balData.monthly_limit ?? "");
        setLowBalanceThreshold(balData.low_balance_threshold ?? "");
        setAutoPause(balData.auto_pause_on_zero ?? false);
      }
    } catch (err) {
      console.error("Failed to load overview:", err);
    }
  }, []);

  const fetchCreditsData = useCallback(async () => {
    setCreditsLoading(true);
    try {
      const pkgs = await getAICreditPackages().catch(() => []);
      setCreditPackages(pkgs || []);
    } catch (err) {
      console.error("Failed to load credits:", err);
    } finally {
      setCreditsLoading(false);
    }
  }, []);

  const fetchCreditActivity = useCallback(async () => {
    setCreditActivityLoading(true);
    try {
      const filters = {
        page: creditTxPage,
        page_size: 10,
      };

      if (creditActivityFilter !== "all") {
        filters.activity_group = creditActivityFilter;
      }

      const txs = await getAITransactions(filters).catch(() => ({ results: [], count: 0 }));
      setCreditTransactions(txs || { results: [], count: 0 });
    } catch (err) {
      console.error("Failed to load credit activity:", err);
    } finally {
      setCreditActivityLoading(false);
    }
  }, [creditTxPage, creditActivityFilter]);

  const fetchPaymentHistory = useCallback(async () => {
    setPaymentHistoryLoading(true);
    try {
      const history = await listPaymentIntents({
        limit: 15,
        offset: paymentPage * 15,
      }).catch(() => ({ results: [], count: 0 }));
      const normalized = history?.data ?? history;
      const results = normalized?.results || normalized || [];
      const count = normalized?.count ?? results.length;
      setPaymentHistory({ results, count });
    } catch (err) {
      console.error("Failed to load payment history:", err);
    } finally {
      setPaymentHistoryLoading(false);
    }
  }, [paymentPage]);

  const fetchMethods = useCallback(async () => {
    try {
      const data = await listPaymentMethods();
      setPaymentMethods(data || []);
    } catch (err) {
      console.error("Failed to load payment methods:", err);
    }
  }, []);

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      await fetchOverview();
      setLoading(false);
    };
    loadAll();
  }, [fetchOverview]);

  useEffect(() => {
    if (activeTab === CREDITS_TAB_KEY) fetchCreditsData();
  }, [activeTab, fetchCreditsData]);

  useEffect(() => {
    if (activeTab === ACTIVITY_TAB_KEY && activityTab === CREDIT_ACTIVITY_TAB_KEY) {
      fetchCreditActivity();
    }
  }, [activeTab, activityTab, fetchCreditActivity]);

  useEffect(() => {
    setCreditTxPage(1);
  }, [creditActivityFilter]);

  useEffect(() => {
    if (activeTab === ACTIVITY_TAB_KEY && activityTab === PAYMENTS_ACTIVITY_TAB_KEY) {
      fetchPaymentHistory();
    }
  }, [activeTab, activityTab, fetchPaymentHistory]);

  useEffect(() => {
    if (activeTab === METHODS_TAB_KEY) fetchMethods();
  }, [activeTab, fetchMethods]);

  // ── Handlers ──────────────────────────────────────────────────────
  const handleTopUp = async (pkg) => {
    setActionLoading(true);
    try {
      const result = await topUpAICredits({ package_id: pkg.id });
      toast.success(
        result?.message || `Successfully added ${pkg.credits} credits!`
      );
      await fetchOverview();
      await fetchCreditsData();
      await fetchCreditActivity();
    } catch (err) {
      toast.error(err.message || "Top-up failed");
    } finally {
      setActionLoading(false);
    }
  };

  const handleSaveAISettings = async () => {
    setActionLoading(true);
    try {
      const data = {};
      if (dailyLimit !== "") data.daily_limit = Number(dailyLimit);
      if (monthlyLimit !== "") data.monthly_limit = Number(monthlyLimit);
      if (lowBalanceThreshold !== "")
        data.low_balance_threshold = Number(lowBalanceThreshold);
      data.auto_pause_on_zero = autoPause;

      await updateAIBalance(data);
      toast.success("Credit settings updated");
      await fetchOverview();
    } catch (err) {
      toast.error(err.message || "Failed to update settings");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteMethod = async (id) => {
    try {
      await deletePaymentMethod(id);
      toast.success("Payment method removed");
      await fetchMethods();
    } catch (err) {
      toast.error(err.message || "Failed to remove payment method");
    }
  };

  const handleSetDefault = async (id) => {
    try {
      await setDefaultPaymentMethod(id);
      toast.success("Default payment method updated");
      await fetchMethods();
    } catch (err) {
      toast.error(err.message || "Failed to update default");
    }
  };

  const balanceAmount = Number(aiBalance?.balance ?? 0);
  const lifetimeCreditsAdded = Number(aiBalance?.lifetime_credits ?? 0);
  const lifetimeCreditsUsed = Number(aiBalance?.lifetime_debits ?? 0);
  const totalCreditsUsed = Number(spending?.total_spent ?? 0);
  const dailyAverageCredits = Number(spending?.daily_average ?? 0);
  const lowBalanceAmount =
    aiBalance?.low_balance_threshold != null
      ? Number(aiBalance.low_balance_threshold)
      : null;
  const hasLowBalanceWarning =
    Boolean(aiBalance?.is_low_balance) ||
    (lowBalanceAmount != null && balanceAmount <= lowBalanceAmount);
  const estimatedRunwayDays =
    dailyAverageCredits > 0 ? balanceAmount / dailyAverageCredits : null;
  const subscriptionName = subscription
    ? subscription.tier_name ||
      subscription.tier?.charAt(0).toUpperCase() + subscription.tier?.slice(1)
    : "No Plan";
  const subscriptionStatus = subscription?.status || "inactive";
  const nextBillingDate = subscription?.current_period_end
    ? new Date(subscription.current_period_end).toLocaleDateString()
    : null;
  const overviewState = hasLowBalanceWarning
    ? {
        badge: "Needs attention",
        badgeVariant: "warning",
        title: "Credits are getting low",
        description:
          "Top up credits soon so scheduled runs and automations are not interrupted.",
      }
    : subscription
      ? {
          badge: "Billing healthy",
          badgeVariant: "info",
          title: "Billing is in good shape",
          description:
            "Your credits balance and recent activity look healthy for normal usage.",
        }
      : {
          badge: "Billing overview",
          badgeVariant: "default",
          title: "Review your billing setup",
          description:
            "Manage credits, review recent activity, and update your plan from the subscription section below.",
        };
  const visibleCreditTransactions = React.useMemo(() => {
    if (creditActivityFilter === "all") {
      return creditTransactions.results || [];
    }

    return (creditTransactions.results || []).filter(
      (tx) => getCreditTransactionGroup(tx.transaction_type) === creditActivityFilter
    );
  }, [creditTransactions.results, creditActivityFilter]);

  // ── Render ────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6 pb-8">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div className="flex items-start gap-3">
          <PageBackButton label="Back to Settings" to="/settings" preferHistory={false} hideLabelOnMobile />
          <div>
          <h1 className="text-2xl font-bold text-textPrimary flex items-center gap-2">
            <DollarSign className="w-6 h-6 text-primary" />
            Billing
          </h1>
          <p className="text-sm text-textSecondary mt-1">
            Subscription, credits, payments, and account controls
          </p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            fetchOverview();
            if (activeTab === CREDITS_TAB_KEY) fetchCreditsData();
            if (activeTab === ACTIVITY_TAB_KEY && activityTab === CREDIT_ACTIVITY_TAB_KEY) fetchCreditActivity();
            if (activeTab === ACTIVITY_TAB_KEY && activityTab === PAYMENTS_ACTIVITY_TAB_KEY) fetchPaymentHistory();
            if (activeTab === METHODS_TAB_KEY) fetchMethods();
          }}
          title="Refresh"
        >
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {/* Tab bar */}
      <Tabs tabs={TABS} activeTab={activeTab} onChange={handleTabChange} />

      {/* ═════════════════════════ OVERVIEW TAB ═════════════════════ */}
      <TabPanel tabKey={OVERVIEW_TAB_KEY} activeTab={activeTab}>
        <div className="space-y-5">
          <Card className="border-primary/20 bg-primary/5">
            <CardContent className="p-6">
              <Badge variant={overviewState.badgeVariant}>{overviewState.badge}</Badge>
              <h2 className="text-2xl font-semibold text-textPrimary mt-3">
                {overviewState.title}
              </h2>
              <p className="text-sm text-textSecondary mt-2 max-w-2xl">
                {overviewState.description}
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-5">
                <div className="rounded-xl border border-borderDefault bg-backgroundSurface p-4">
                  <p className="text-xs uppercase tracking-wider text-textSecondary">
                    Credits Available
                  </p>
                  <p className="text-2xl font-semibold text-textPrimary mt-2">
                    {balanceAmount.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </p>
                  <p className="text-sm text-textSecondary mt-1">ready to use</p>
                </div>

                <div className="rounded-xl border border-borderDefault bg-backgroundSurface p-4">
                  <p className="text-xs uppercase tracking-wider text-textSecondary">
                    30-Day Used
                  </p>
                  <p className="text-2xl font-semibold text-textPrimary mt-2">
                    {totalCreditsUsed.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </p>
                  <p className="text-sm text-textSecondary mt-1">
                    {dailyAverageCredits > 0
                      ? `${dailyAverageCredits.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })} per day`
                      : "no recent usage"}
                  </p>
                </div>

                <div className="rounded-xl border border-borderDefault bg-backgroundSurface p-4">
                  <p className="text-xs uppercase tracking-wider text-textSecondary">
                    Estimated Runway
                  </p>
                  <p className="text-2xl font-semibold text-textPrimary mt-2">
                    {estimatedRunwayDays != null
                      ? estimatedRunwayDays.toLocaleString(undefined, {
                          maximumFractionDigits: 1,
                        })
                      : "-"}
                  </p>
                  <p className="text-sm text-textSecondary mt-1">
                    {estimatedRunwayDays != null ? "days at recent pace" : "waiting for usage data"}
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap gap-3 mt-6">
                <Button size="sm" onClick={openCreditsTab}>
                  Add Credits
                </Button>
                <Button variant="outline" size="sm" onClick={openCreditActivity}>
                  Review Activity
                </Button>
                <Button variant="ghost" size="sm" onClick={openPaymentsActivity}>
                  View Payments
                </Button>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <Card>
              <CardContent className="px-6 pt-6 pb-6 h-full flex flex-col">
                <div className="flex items-start gap-3 mb-5">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Shield className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-xs text-textSecondary uppercase tracking-wider">
                      Subscription
                    </p>
                    <p className="text-lg font-semibold text-textPrimary">{subscriptionName}</p>
                  </div>
                </div>
                <div className="space-y-3 text-sm flex-1">
                  <div className="flex items-center justify-between gap-4 rounded-lg border border-borderDefault px-3 py-2.5">
                    <span className="text-textSecondary">Status</span>
                    <Badge variant={subscriptionStatus === "active" ? "success" : "warning"}>
                      {subscriptionStatus}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between gap-4 rounded-lg border border-borderDefault px-3 py-2.5">
                    <span className="text-textSecondary">Next billing</span>
                    <span className="font-medium text-textPrimary">{nextBillingDate || "No renewal scheduled"}</span>
                  </div>
                </div>
                <Button
                  size="md"
                  className="mt-5 w-full min-h-11"
                  onClick={() => (window.location.href = "/subscription")}
                >
                  Manage Plan
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="px-6 pt-6 pb-6 h-full flex flex-col">
                <div className="flex items-start gap-4 mb-5">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Settings className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-xs text-textSecondary uppercase tracking-wider">
                        Credit Controls
                      </p>
                      <p className="text-lg font-semibold text-textPrimary">
                        {hasLowBalanceWarning ? "Monitor balance" : "Controls in place"}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="space-y-3 text-sm flex-1">
                  <div className="flex items-center justify-between gap-4 rounded-lg border border-borderDefault px-3 py-2.5">
                    <span className="text-textSecondary">Low balance warning</span>
                    <span className="font-medium text-textPrimary">
                      {lowBalanceAmount != null ? `${lowBalanceAmount.toLocaleString()} credits` : "Not set"}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-4 rounded-lg border border-borderDefault px-3 py-2.5">
                    <span className="text-textSecondary">Auto-pause at zero</span>
                    <Badge variant={autoPause ? "warning" : "default"}>
                      {autoPause ? "Enabled" : "Disabled"}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between gap-4 rounded-lg border border-borderDefault px-3 py-2.5">
                    <span className="text-textSecondary">Lifetime added</span>
                    <span className="font-medium text-textPrimary">{lifetimeCreditsAdded.toLocaleString()} credits</span>
                  </div>
                  <div className="flex items-center justify-between gap-4 rounded-lg border border-borderDefault px-3 py-2.5">
                    <span className="text-textSecondary">Lifetime used</span>
                    <span className="font-medium text-textPrimary">{lifetimeCreditsUsed.toLocaleString()} credits</span>
                  </div>
                </div>
                <Button
                  size="md"
                  className="mt-5 w-full min-h-11"
                  onClick={openCreditControls}
                >
                  Manage Credit Settings
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </TabPanel>

      {/* ═══════════════════════ USAGE TAB ═══════════════════ */}
      <TabPanel tabKey={USAGE_TAB_KEY} activeTab={activeTab}>
        <CreditUsageShell />
      </TabPanel>

      {/* ═══════════════════════ CREDITS TAB ═══════════════════ */}
      <TabPanel tabKey={CREDITS_TAB_KEY} activeTab={activeTab}>
        <div className="space-y-6">
          <div>
            <h2 className="text-lg font-semibold text-textPrimary">Credits</h2>
            <p className="text-sm text-textSecondary mt-1">
              Keep enough credits available for runs and automations, then open advanced controls only if you need extra safeguards.
            </p>
          </div>

          <Card className="border-primary/20 bg-primary/5">
            <CardContent className="p-5 space-y-5 lg:space-y-0 lg:grid lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)] lg:gap-6 lg:items-start">
              <div>
                <Badge variant={hasLowBalanceWarning ? "warning" : "info"}>
                  {hasLowBalanceWarning ? "Action Recommended" : "Balance Healthy"}
                </Badge>
                <h3 className="text-xl font-semibold text-textPrimary mt-3">
                  Stay ahead of credit interruptions
                </h3>
                <p className="text-sm text-textSecondary mt-2 max-w-2xl">
                  Review your balance and recent usage first, top up when needed,
                  and leave the optional safeguards collapsed unless you want
                  automated spending guardrails.
                </p>
                <div className="flex flex-wrap gap-3 mt-4">
                  <Button size="sm" onClick={openCreditActivity}>
                    View Credit Activity
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleTabChange(METHODS_TAB_KEY)}
                  >
                    Payment Methods
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="rounded-lg border border-borderDefault bg-backgroundSurface p-4">
                  <p className="text-xs uppercase tracking-wider text-textSecondary">
                    Available Now
                  </p>
                  <p className="mt-2 text-2xl font-semibold text-textPrimary">
                    {balanceAmount.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </p>
                  <p className="text-sm text-textSecondary mt-1">credits</p>
                </div>

                <div className="rounded-lg border border-borderDefault bg-backgroundSurface p-4">
                  <p className="text-xs uppercase tracking-wider text-textSecondary">
                    Daily Average
                  </p>
                  <p className="mt-2 text-2xl font-semibold text-textPrimary">
                    {dailyAverageCredits.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </p>
                  <p className="text-sm text-textSecondary mt-1">credits / day</p>
                </div>

                <div className="rounded-lg border border-borderDefault bg-backgroundSurface p-4">
                  <p className="text-xs uppercase tracking-wider text-textSecondary">
                    Estimated Runway
                  </p>
                  <p className="mt-2 text-2xl font-semibold text-textPrimary">
                    {estimatedRunwayDays != null
                      ? estimatedRunwayDays.toLocaleString(undefined, {
                          maximumFractionDigits: 1,
                        })
                      : "-"}
                  </p>
                  <p className="text-sm text-textSecondary mt-1">
                    {estimatedRunwayDays != null ? "days at recent pace" : "no recent usage yet"}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Balance health + spending chart */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* Balance health */}
            <Card>
              <CardContent className="p-5">
                <h3 className="text-sm font-semibold text-textSecondary uppercase tracking-wider mb-3">
                  Balance Health
                </h3>
                <div className="space-y-3 text-sm">
                  <div className="flex items-center justify-between gap-4 rounded-lg border border-borderDefault px-3 py-2.5">
                    <span className="text-textSecondary">Warning Threshold</span>
                    <span className="font-medium text-textPrimary">
                      {lowBalanceAmount != null
                        ? `${lowBalanceAmount.toLocaleString()} credits`
                        : "Not set"}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-4 rounded-lg border border-borderDefault px-3 py-2.5">
                    <span className="text-textSecondary">Auto-pause on zero</span>
                    <Badge variant={autoPause ? "warning" : "default"}>
                      {autoPause ? "Enabled" : "Disabled"}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between gap-4 rounded-lg border border-borderDefault px-3 py-2.5">
                    <span className="text-textSecondary">Lifetime Added</span>
                    <span className="font-medium text-textPrimary">
                      {lifetimeCreditsAdded.toLocaleString()} credits
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-4 rounded-lg border border-borderDefault px-3 py-2.5">
                    <span className="text-textSecondary">Lifetime Used</span>
                    <span className="font-medium text-textPrimary">
                      {lifetimeCreditsUsed.toLocaleString()} credits
                    </span>
                  </div>
                </div>
                {hasLowBalanceWarning && (
                  <div className="mt-4 flex items-center gap-2 text-sm text-yellow-400">
                    <AlertTriangle className="w-4 h-4" />
                    Your balance is at or below the warning threshold.
                  </div>
                )}
                <Button variant="outline" size="sm" className="mt-4" onClick={openCreditActivity}>
                  View Credit Activity
                </Button>
              </CardContent>
            </Card>

            {/* Spending chart */}
            <SpendingChart spending={spending} />
          </div>

          {/* Credit packages */}
          <div>
            <h3 className="text-lg font-semibold text-textPrimary mb-3">
              Add Credits
            </h3>
            <p className="text-sm text-textSecondary mb-3">
              Choose a package when you need more credits. Your payment details are collected at checkout and saved for future purchases.
            </p>
            {creditsLoading ? (
              <Card>
                <CardContent className="p-6 flex items-center justify-center text-textSecondary gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading credit options...
                </CardContent>
              </Card>
            ) : creditPackages.length === 0 ? (
              <Card>
                <CardContent className="p-6 text-center text-textSecondary">
                  No credit packages available at this time.
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {creditPackages.map((pkg) => (
                  <CreditPackageCard
                    key={pkg.id}
                    pkg={pkg}
                    onPurchase={handleTopUp}
                    loading={actionLoading}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Advanced credit controls */}
          <details
            ref={advancedCreditControlsRef}
            className="group"
            open={isAdvancedCreditControlsOpen}
            onToggle={(event) => setIsAdvancedCreditControlsOpen(event.currentTarget.open)}
          >
            <summary className="list-none cursor-pointer">
              <Card className="border-dashed border-borderDefault group-open:border-primary/40">
                <CardContent className="p-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <h3 className="text-sm font-semibold text-textPrimary flex items-center gap-2">
                      <Settings className="w-4 h-4" />
                      Advanced Credit Controls
                    </h3>
                    <p className="text-sm text-textSecondary mt-1">
                      Optional limits and auto-pause rules. Most users can leave these unchanged.
                    </p>
                  </div>
                  <Badge variant="default">Optional</Badge>
                </CardContent>
              </Card>
            </summary>

            <Card className="mt-4">
              <CardContent className="p-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-textSecondary mb-1">
                      Daily Limit
                    </label>
                    <input
                      type="number"
                      className="w-full rounded-md border border-borderDefault bg-backgroundSurface text-textPrimary px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                      value={dailyLimit}
                      onChange={(e) => setDailyLimit(e.target.value)}
                      placeholder="No limit"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-textSecondary mb-1">
                      Monthly Limit
                    </label>
                    <input
                      type="number"
                      className="w-full rounded-md border border-borderDefault bg-backgroundSurface text-textPrimary px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                      value={monthlyLimit}
                      onChange={(e) => setMonthlyLimit(e.target.value)}
                      placeholder="No limit"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-textSecondary mb-1">
                      Low Balance Threshold
                    </label>
                    <input
                      type="number"
                      className="w-full rounded-md border border-borderDefault bg-backgroundSurface text-textPrimary px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                      value={lowBalanceThreshold}
                      onChange={(e) => setLowBalanceThreshold(e.target.value)}
                      placeholder="10"
                    />
                  </div>
                  <div className="flex items-center justify-between gap-3 rounded-lg border border-borderDefault px-4 py-3">
                    <label className="text-sm text-textSecondary">
                      Auto-pause when balance is zero
                    </label>
                    <button
                      onClick={() => setAutoPause(!autoPause)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        autoPause ? "bg-primary" : "bg-secondary/40"
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          autoPause ? "translate-x-6" : "translate-x-1"
                        }`}
                      />
                    </button>
                  </div>
                </div>
                <div className="mt-4 flex justify-end">
                  <Button
                    size="sm"
                    onClick={handleSaveAISettings}
                    disabled={actionLoading}
                  >
                    {actionLoading ? "Saving..." : "Save Controls"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </details>
        </div>
      </TabPanel>

      {/* ═══════════════════════ ACTIVITY TAB ═══════════════════ */}
      <TabPanel tabKey={ACTIVITY_TAB_KEY} activeTab={activeTab}>
        <div className="space-y-6">
          <div>
            <h2 className="text-lg font-semibold text-textPrimary">Activity</h2>
            <p className="text-sm text-textSecondary mt-1">
              Review payment events separately from credit consumption so it is clear what changed and why.
            </p>
          </div>

          <Tabs tabs={ACTIVITY_TABS} activeTab={activityTab} onChange={setActivityTab} />

          <TabPanel tabKey={CREDIT_ACTIVITY_TAB_KEY} activeTab={activityTab}>
            {creditActivityLoading ? (
              <Card>
                <CardContent className="p-8 flex items-center justify-center gap-2 text-textSecondary">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading credit activity...
                </CardContent>
              </Card>
            ) : (
              <Card>
                <div className="px-4 pt-4 pb-3 border-b border-borderDefault space-y-3">
                  <div className="flex flex-wrap items-center gap-2">
                    {CREDIT_ACTIVITY_FILTERS.map((option) => {
                      const isActive = creditActivityFilter === option.key;

                      return (
                        <Button
                          key={option.key}
                          variant={isActive ? "default" : "outline"}
                          size="sm"
                          className="rounded-full"
                          onClick={() => setCreditActivityFilter(option.key)}
                        >
                          {option.label}
                        </Button>
                      );
                    })}
                  </div>
                  <p className="text-sm text-textSecondary">
                    {creditActivityFilter === "all"
                      ? "All recent credit changes."
                      : creditActivityFilter === "added"
                        ? "Balance increases from top-ups, refunds, or credits."
                        : "Balance consumption from runs, automations, or other debits."}
                      <span className="ml-1.5">
                        Showing {visibleCreditTransactions.length} matching entries on this page.
                      </span>
                  </p>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-borderDefault">
                        <th className="text-left px-4 py-3 text-textSecondary font-medium">
                          Date
                        </th>
                        <th className="text-left px-4 py-3 text-textSecondary font-medium">
                          Type
                        </th>
                        <th className="text-left px-4 py-3 text-textSecondary font-medium">
                          Description
                        </th>
                        <th className="text-right px-4 py-3 text-textSecondary font-medium">
                          Amount
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleCreditTransactions.length > 0 ? (
                        visibleCreditTransactions.map((tx) => {
                          const presentation = getCreditTransactionPresentation(
                            tx.transaction_type,
                            tx.transaction_type_display
                          );

                          return (
                            <tr
                              key={tx.id}
                              className="border-b border-borderDefault/50 hover:bg-secondary/5"
                            >
                              <td className="px-4 py-3 text-textSecondary">
                                {new Date(tx.created_at).toLocaleDateString()}
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex flex-col items-start gap-1">
                                  <Badge variant={presentation.variant}>
                                    {presentation.label}
                                  </Badge>
                                  <span className="text-xs text-textSecondary">
                                    {presentation.detail}
                                  </span>
                                </div>
                              </td>
                              <td className="px-4 py-3 text-textPrimary">
                                {tx.description || tx.reference_type || "—"}
                              </td>
                              <td
                                className={`px-4 py-3 text-right font-medium ${presentation.amountClassName}`}
                              >
                                {presentation.sign}
                                {Number(tx.amount).toLocaleString(undefined, {
                                  minimumFractionDigits: 2,
                                })}
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan={4} className="px-4 py-8 text-center text-textSecondary">
                            {creditActivityFilter === "all"
                              ? "No credit activity yet."
                              : "No credit activity matches this filter yet."}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {(creditTransactions.total_pages > 1 || creditTransactions.count > 10) && (
                  <div className="flex items-center justify-between px-4 py-3 border-t border-borderDefault">
                    <p className="text-sm text-textSecondary">
                      Page {creditTxPage} of {creditTransactions.total_pages || Math.ceil((creditTransactions.count || 0) / 10)} ({creditTransactions.count} total)
                    </p>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={creditTxPage <= 1}
                        onClick={() => setCreditTxPage(creditTxPage - 1)}
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={creditTxPage >= (creditTransactions.total_pages || Math.ceil((creditTransactions.count || 0) / 10))}
                        onClick={() => setCreditTxPage(creditTxPage + 1)}
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </Card>
            )}
          </TabPanel>

          <TabPanel tabKey={PAYMENTS_ACTIVITY_TAB_KEY} activeTab={activityTab}>
            {paymentHistoryLoading ? (
              <Card>
                <CardContent className="p-8 flex items-center justify-center gap-2 text-textSecondary">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading payment history...
                </CardContent>
              </Card>
            ) : (
              <PaymentHistoryTable
                intents={paymentHistory.results}
                total={paymentHistory.count}
                page={paymentPage}
                onPageChange={setPaymentPage}
                pageSize={15}
              />
            )}
          </TabPanel>
        </div>
      </TabPanel>

      {/* ════════════════════ PAYMENT METHODS TAB ═════════════════ */}
      <TabPanel tabKey={METHODS_TAB_KEY} activeTab={activeTab}>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-textPrimary">
              Saved Payment Methods
            </h3>
            <Badge variant="info">Saved automatically after checkout</Badge>
          </div>

          {paymentMethods.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <CreditCard className="w-12 h-12 text-textSecondary/30 mx-auto mb-3" />
                <p className="text-textSecondary">
                  No payment methods saved yet. Payment methods are
                  automatically saved when you make a purchase with Stripe.
                </p>
                <div className="mt-4 flex flex-wrap items-center justify-center gap-3">
                  <Button size="sm" onClick={openCreditsTab}>
                    Buy Credits
                  </Button>
                  <Button variant="outline" size="sm" onClick={openPaymentsActivity}>
                    View Payments
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {paymentMethods.map((method) => (
                <PaymentMethodCard
                  key={method.id}
                  method={method}
                  onDelete={handleDeleteMethod}
                  onSetDefault={handleSetDefault}
                />
              ))}
            </div>
          )}
        </div>
      </TabPanel>
    </div>
  );
}
